import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Edit2, Calendar, Folder } from 'lucide-react';
import { ArrowLeft, User, Eye } from '../components/icons';
import api from '../services/api';
import { createKbObjectUrl } from '../utils/knowledgeFileProxy';

export default function ArticuloBaseConocimiento() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [articulo, setArticulo] = useState(null);
    const contentRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/baseconocimiento/${id}`);
                // Pre-procesar HTML para evitar primer request 401 a /preview
                let contenido = res.data?.contenido || '';
                if (contenido) {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(contenido, 'text/html');
                    const imgs = Array.from(doc.querySelectorAll('img'));
                    for (const img of imgs) {
                        const original = img.getAttribute('src') || '';
                        let normalized = original;
                        if (original.includes('/securefile/kb-signed')) {
                            try {
                                const url = new URL(original, window.location.origin);
                                const f = url.searchParams.get('f');
                                if (f) normalized = `${url.origin}/api/securefile/preview/baseconocimiento/${f}`;
                            } catch { }
                        }
                        if (normalized.includes('/securefile/preview/baseconocimiento/')) {
                            try {
                                const objectUrl = await createKbObjectUrl(normalized);
                                img.setAttribute('src', objectUrl);
                            } catch { }
                        }
                    }
                    contenido = DOMPurify.sanitize(doc.body.innerHTML);
                }
                setArticulo({ ...res.data, contenido });
            } catch (e) {
                navigate('/base-conocimiento');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, navigate]);

    // Igual que otros apartados (tickets): convertir imágenes protegidas a blob con token
    useEffect(() => {
        const hydrateImages = async () => {
            const root = contentRef.current;
            if (!root) return;
            const imgs = Array.from(root.querySelectorAll('img'));
            for (const img of imgs) {
                const src = img.getAttribute('src') || '';
                let normalized = src;
                // Si viene firmado, convertir a preview permanente
                if (src.includes('/securefile/kb-signed')) {
                    try {
                        const url = new URL(src, window.location.origin);
                        const f = url.searchParams.get('f');
                        if (f) normalized = `${url.origin}/api/securefile/preview/baseconocimiento/${f}`;
                    } catch { }
                }
                if (!normalized.includes('/securefile/preview/baseconocimiento/')) continue;
                try {
                    const blobUrl = await createKbObjectUrl(normalized);
                    img.setAttribute('src', blobUrl);
                } catch (e) {
                    // Si falla, dejamos el src original
                }
            }
        };
        hydrateImages();
    }, [articulo?.contenido]);

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
        );
    }

    if (!articulo) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">Artículo no encontrado</div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Volver a Base de Conocimiento
                </button>

                {(user?.role === 'admin' || user?.role === 'soporte') && (
                    <button
                        aria-label="Editar artículo"
                        title="Editar artículo"
                        onClick={() => navigate(`/base-conocimiento/editar/${id}`)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    >
                        <Edit2 className="w-4 h-4" />
                        <span>Editar Artículo</span>
                    </button>
                )}
            </div>

            <article className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                {/* Header del Artículo */}
                <div className="px-6 sm:px-10 py-8 border-b border-gray-100 dark:border-gray-700/50">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/30 dark:border-blue-800/50 dark:text-blue-400">
                            <Folder className="w-3.5 h-3.5 mr-1" />
                            {articulo.categoria || 'General'}
                        </span>
                        {articulo.esPublico && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:border-green-800/50 dark:text-green-400">
                                Público
                            </span>
                        )}
                    </div>

                    <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-4">
                        {articulo.titulo}
                    </h1>

                    {articulo.descripcion && (
                        <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 font-medium leading-relaxed">
                            {articulo.descripcion}
                        </p>
                    )}

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-400 font-medium">
                        {articulo.autor && (
                            <div className="flex items-center">
                                <User className="w-4 h-4 mr-1.5 text-gray-400" />
                                {articulo.autor}
                            </div>
                        )}
                        <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1.5 text-gray-400" />
                            {new Date(articulo.fechaCreacion).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                            <Eye className="w-4 h-4 mr-1.5 text-gray-400" />
                            {articulo.visitas} lecturas
                        </div>
                    </div>
                </div>

                {/* Contenido del Artículo */}
                <div className="px-6 sm:px-10 py-8">
                    <div
                        ref={contentRef}
                        className="prose prose-blue dark:prose-invert max-w-none text-gray-800 dark:text-gray-200"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(articulo.contenido) }}
                    />
                </div>
            </article>
        </div>
    );
}


